import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }
  }, [darkMode]);

  useEffect(() => {
    try {
      const el = document.documentElement;
      const tokens = darkMode ? {
        '--ink': '#EEF1F6',
        '--paper': '#0C1424',
        '--surface': '#131D33',
        '--border': '#26324D',
        '--primary': '#7EA0FF',
        '--on-primary': '#0C1424',
        '--gold': '#E8A317',
        '--gold-text': '#0F1B2D',
        '--waec': '#F08A7C',
        '--jamb': '#5CC6C0',
        '--success': '#6CCB93',
        '--muted': '#9BA6BF',
        '--footer': '#080E1B',
      } : {
        '--ink': '#0F1B2D',
        '--paper': '#F5F6F8',
        '--surface': '#FFFFFF',
        '--border': '#DDE1E8',
        '--primary': '#1E3A8A',
        '--on-primary': '#FFFFFF',
        '--gold': '#E8A317',
        '--gold-text': '#0F1B2D',
        '--waec': '#B3372B',
        '--jamb': '#0F6B6B',
        '--success': '#2F7D4F',
        '--muted': '#55607A',
        '--footer': '#0F1B2D',
      };

      Object.entries(tokens).forEach(([key, value]) => {
        el.style.setProperty(key, value);
      });
      el.style.colorScheme = darkMode ? 'dark' : 'light';
      el.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    } catch (e) {
      // ignore
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev: boolean) => !prev);
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#7EA0FF' : '#1E3A8A',
        light: darkMode ? '#9EBBFF' : '#3557BB',
        dark: darkMode ? '#5E88EF' : '#153777',
        contrastText: darkMode ? '#0C1424' : '#FFFFFF',
      },
      secondary: {
        main: darkMode ? '#5CC6C0' : '#0F6B6B',
        light: darkMode ? '#8BE3E0' : '#2B8585',
        dark: darkMode ? '#45ACAC' : '#0A4F4F',
        contrastText: darkMode ? '#0C1424' : '#FFFFFF',
      },
      success: {
        main: darkMode ? '#6CCB93' : '#2F7D4F',
        light: darkMode ? '#9BE4B7' : '#4D9965',
        dark: darkMode ? '#4EAC75' : '#205D38',
      },
      warning: {
        main: '#E8A317',
        light: '#F0C35D',
        dark: '#C98507',
        contrastText: '#0F1B2D',
      },
      error: {
        main: darkMode ? '#F08A7C' : '#B3372B',
        light: '#F5A79C',
        dark: '#9E2E24',
      },
      background: {
        default: darkMode ? '#0C1424' : '#F5F6F8',
        paper: darkMode ? '#131D33' : '#FFFFFF',
      },
      text: {
        primary: darkMode ? '#EEF1F6' : '#0F1B2D',
        secondary: darkMode ? '#9BA6BF' : '#55607A',
        disabled: darkMode ? '#64738F' : '#8C93A6',
      },
      divider: darkMode ? '#26324D' : '#DDE1E8',
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      h1: {
        fontFamily: '"Fraunces", serif',
        fontWeight: 600,
        fontSize: '2.8rem',
        letterSpacing: '-0.03em',
        lineHeight: 1.15,
        color: darkMode ? '#EEF1F6' : '#0F1B2D',
      },
      h2: {
        fontFamily: '"Fraunces", serif',
        fontWeight: 600,
        fontSize: '2.2rem',
        lineHeight: 1.2,
        color: darkMode ? '#EEF1F6' : '#0F1B2D',
      },
      h3: {
        fontFamily: '"Fraunces", serif',
        fontWeight: 600,
        fontSize: '1.6rem',
        lineHeight: 1.2,
        color: darkMode ? '#EEF1F6' : '#0F1B2D',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.3,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '0.9rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.95rem',
            padding: '10px 18px',
            minHeight: 44,
            transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
            '&:focus-visible': {
              outline: `2px solid ${darkMode ? '#E8A317' : '#E8A317'}`,
              outlineOffset: 2,
            },
          },
          contained: {
            backgroundColor: darkMode ? '#7EA0FF' : '#1E3A8A',
            color: darkMode ? '#0C1424' : '#FFFFFF',
            '&:hover': {
              backgroundColor: darkMode ? '#94B1FF' : '#163574',
            },
          },
          outlined: {
            borderColor: darkMode ? '#7EA0FF' : '#1E3A8A',
            color: darkMode ? '#7EA0FF' : '#1E3A8A',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(126,160,255,0.08)' : 'rgba(30,58,138,0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: darkMode ? '1px solid #26324D' : '1px solid #DDE1E8',
            boxShadow: 'none',
            transition: 'border-color 0.2s ease, transform 0.2s ease',
            '&:hover': {
              borderColor: darkMode ? '#4A5E8D' : '#BFC7D3',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#131D33' : '#FFFFFF',
            color: darkMode ? '#EEF1F6' : '#0F1B2D',
            borderBottom: darkMode ? '1px solid #26324D' : '1px solid #DDE1E8',
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '&:hover fieldset': {
                borderColor: darkMode ? '#7EA0FF' : '#1E3A8A',
              },
              '&.Mui-focused fieldset': {
                borderColor: darkMode ? '#E8A317' : '#E8A317',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
          filled: {
            backgroundColor: darkMode ? '#334155' : '#e2e8f0',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
